import { useState } from 'react';
import { useAddDepartmentMutation } from 'redux/features/departmentApi';
import { toast } from 'react-toastify';

import Icon from '../../../components/AppIcon';

const AddDepartmentModal = ({ isOpen, onClose, onDepartmentAdded }) => {
  const [formData, setFormData] = useState({ name: '' });
  const [addDepartment, { isLoading }] = useAddDepartmentMutation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name?.trim()) {
      alert('Please enter a department name');
      return;
    }

    try {
      const result = await addDepartment({ name: formData?.name?.trim() }).unwrap();

      // If successful
      if (result) {
        toast.success('Department added successfully!');
        setFormData({ name: '' });
        onDepartmentAdded();
        onClose();
      }
    } catch (error) {
      console.error('Error adding department:', error);
    }
  };

  const handleClose = () => {
    setFormData({ name: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Building" size={24} color="var(--color-primary)" />
            <h3 className="text-lg font-medium text-gray-900">Add New Department</h3>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter department name"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Department names must be unique within the organization
            </p>
            <p className="text-xs text-red-500 mt-1">
              {formData?.name?.length > 50 && 'Department name must be at most 50 characters long'}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData?.name?.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Department
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDepartmentModal;
